/**
 * Dashboard API - Analysis, License, and Content Management
 */

import axios from 'axios';
import api from './api';

// ============================================
// TYPES
// ============================================

export interface LicenseStatus {
  tier: string;
  status: string;
  expiresAt?: string;
  productSku?: string;
  maxSites?: number;
  hasStoredKey?: boolean;
  storedKeyMasked?: string | null;
}

export interface ClaudeApiStatus {
  hasApiKey: boolean;
  apiKeyMasked: string | null;
  status: 'valid' | 'invalid' | 'untested';
}

export interface BlogTag {
  id: string;
  name: string;
  postCount: number;
  englishCount?: number;
  nonEnglishCount?: number;
  englishPostIds?: string[];  // Array of English post IDs for unique counting
  languages?: Record<string, number>;  // e.g., { en: 10, fr: 2 }
}

export interface AnalysisQueue {
  sessionId: string;
  status: string;
  progress: number;
  progressMessage: string;
  progressDetails?: string;   // Granular detail from BKA (e.g. "Analyzing: Content Marketing (3/8)")
  errorMessage?: string;      // Error message from BKA (e.g., BILLING_ERROR when API credits depleted)
  postsCount: number;
  postsUploaded: number;
  startedAt: string;
  structuralExcluded?: number; // Posts excluded due to structural-only tags
}

export interface QueueEntry {
  id: number;
  sessionId: string;
  analysisType: string;
  status: string;
  postsCount: number;
  postsUploaded: number;
  progress: number;
  progressMessage: string;
  startedAt: string;
  completedAt?: string;
  lastActivityAt: string;
  createdAt: string;
  isStuck: boolean;
}

export interface AffectedPost {
  post_id: number;
  post_title: string;
  current_status: string;
  estimated_points?: number;
  will_become?: string;
}

export interface StrategicRecommendation {
  action: string;
  impact: string;
  priority: 'high' | 'medium' | 'low';
  posts_affected?: number;
  details?: {
    affected_posts?: AffectedPost[];
    effort_estimate?: 'quick' | 'moderate' | 'extended';
    estimated_total_impact?: string;
    badge_progress?: string[];
    recommendation_ids?: string[];
  };
}

export interface TierDistribution {
  isolated?: number;
  emerging_connections?: number;
  well_connected?: number;
  content_hubs?: number;
  isolated_percentage?: number;
  emerging_connections_percentage?: number;
  well_connected_percentage?: number;
  content_hubs_percentage?: number;
  // camelCase alternatives
  emergingConnections?: number;
  wellConnected?: number;
  contentHubs?: number;
  isolatedPercentage?: number;
  emergingConnectionsPercentage?: number;
  wellConnectedPercentage?: number;
  contentHubsPercentage?: number;
}

export interface ContentHealthSummary {
  headline?: string;
  dynamicNarrative?: string;
  status_summary?: string;
  tierDistribution?: TierDistribution;
  tier_distribution?: TierDistribution;  // snake_case version
  averageConnectivityScore?: number;
  postsReadyToPromote?: number;
  badgesAwarded?: number;
  recommendations?: StrategicRecommendation[];
  scoring_insights?: {
    general_tips?: string[];
    badge_criteria?: {
      badge_name: string;
      badge_type: string;
      badge_description: string;
      general_requirements: string;
    }[];
    tier_thresholds?: {
      content_hub: number;
      well_connected: number;
      emerging_connections: number;
    };
  };
}

// Hub Posts Overview (for Dashboard display)
export interface HubPostOverview {
  postId: number;
  postTitle: string;
  hubCategory: string;
  currentPoints: number;
  targetPoints: number;
  hubStatus: 'under_connected' | 'adequate' | 'strong';
}

/** Cost breakdown returned by the BKA API (July 2026+). */
export interface CostSummary {
  /** Combined input + output tokens consumed across all AI calls. */
  totalTokens: number;
  /** Prompt tokens sent to the AI model (may be null for pre-July-2026 records). */
  totalInputTokens: number | null;
  /** Completion tokens returned by the AI model (may be null for pre-July-2026 records). */
  totalOutputTokens: number | null;
  /** Number of individual AI API calls made during the analysis. */
  totalAiCalls: number | null;
  /** Estimated cost in USD, rounded to 2 decimal places. */
  estimatedCostUsd: number | null;
}

export interface AnalysisResult {
  id: number;
  sessionId: string;
  analysisType?: 'Baseline' | 'Incremental';
  executiveSummary: string;
  postsAnalyzed: number;
  postsUploaded?: number;
  // Incremental analysis stats
  postsNew?: number;
  postsChanged?: number;
  postsDeleted?: number;
  postsUnchanged?: number;
  createdAt: string;
  /** Present on analyses completed after July 2026 BKA deployment; null for older records. */
  costSummary?: CostSummary | null;
  contentHealthSummary?: ContentHealthSummary;
  categoryRecommendations?: CategoryRecommendations;
  hubPostsOverview?: HubPostOverview[];
}

// Strategic Category Recommendations (from BKA API)
export interface CategoryRecommendations {
  strategic_assessment?: {
    primary_challenge?: string;
    biggest_opportunity?: string;
  };
  recommended_strategy?: {
    strategy_type?: string;
    priority_level?: 'critical' | 'high' | 'medium' | 'low';
    expected_impact?: string;
    rationale?: string;
  };
  specific_recommendations?: SpecificRecommendation[];
  analysis_metadata?: {
    total_posts?: number;
    categories_requiring_splits?: number;
    categories_ready_for_optimization?: number;
    confidence_level?: 'high' | 'medium' | 'low';
    primary_strategy_rationale?: string;
  };
  mandatory_size_audit?: {
    critical_action_required?: string;
  };
}

export interface SpecificRecommendation {
  action_type: string;
  target_categories?: string | string[];
  action_priority?: 'critical' | 'high' | 'medium' | 'low';
  implementation_complexity?: 'quick' | 'moderate' | 'extended';
  business_justification?: string;
  timeline?: string;
  detailed_plan?: {
    current_state?: string;
    proposed_change?: string;
    expected_outcome?: string;
  };
  split_implementation?: SplitImplementation | Record<string, SplitImplementation>;
}

export interface SplitImplementation {
  original_category?: string;
  content_island_rename?: string;
  new_categories?: NewCategorySpec[];
  affected_post_ids?: number[];
}

export interface NewCategorySpec {
  category_name: string;
  description?: string;
  post_ids?: number[];
  post_count?: number;
  post_titles?: Record<string, string>; // { "postId": "Post Title" } — enriched server-side
}

// Impact data for a single post in a link recommendation
export interface PostImpact {
  points: number;
  will_tier_up: boolean;
  new_tier: string | null;
  confidence: 'high' | 'medium' | 'low';
}

export interface LinkRecommendation {
  id: number;
  sourcePostId: number;
  sourcePostTitle: string;
  targetPostId: number;
  targetPostTitle: string;
  anchorText: string;
  relevanceScore: number;
  recommendationReason: string;
  fromTag: string;
  toTag: string;
  linkType: string;
  priorityLevel: string;
  priorityScore: number;
  impactPoints: number | null;  // MAX of source/target for sorting
  sourcePostImpact: PostImpact | null;  // Impact to FROM post (adds outbound link)
  targetPostImpact: PostImpact | null;  // Impact to TO post (receives inbound link)
  status: string;
  relationshipType: string;
  categoryPair?: string;
  createdAt: string;
  linkAppliedDate?: string;  // Date when link was marked as applied/implemented
  // Semantic link fields
  semanticLinkRequested?: boolean;
  optimalPlacement?: string;
  matchAnchor?: string;
  currentParagraph?: string;
  recommendedReplacement?: string;
  placementRationale?: string;
  confidence?: number;
  targetPostUrl?: string;
  sourcePostUrl?: string;
  // Hub metadata (v2.0)
  sourceIsHub?: boolean;
  sourceHubCategory?: string | null;
  sourceHubStatus?: 'under_connected' | 'adequate' | 'strong' | null;
  targetIsHub?: boolean;
  targetHubCategory?: string | null;
  targetHubStatus?: 'under_connected' | 'adequate' | 'strong' | null;
  involvesHub?: boolean;
}

export interface LinkRecommendationStats {
  total: number;
  pending: number;
  applied: number;
  dismissed: number;
  verified: number;
}

export interface BlogPost {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  publishDate: string;
  updatedAt: string;
  status: string;
  tagIds: string[];
}

// ============================================
// CONTENT ANALYSIS TYPES
// ============================================

export interface TopicCluster {
  cluster_name: string;
  cluster_strength: number;  // 0-1 scale, display as 0-10
  hub_post: number;          // Post ID
  cluster_posts: number[];   // Post IDs
  description?: string;
  all_post_titles?: Record<string, string>;
}

export interface SemanticRelationship {
  source_post_id: number;
  target_post_id: number;
  source_post_title?: string;
  target_post_title?: string;
  relationship_type: string;
  relationship_strength: number;  // 0-1 scale
  relationship_explanation?: string;
  link_direction?: 'unidirectional' | 'bidirectional';
}

// Action types for posts requiring attention
export type PostActionType = 'update' | 'rewrite' | 'retire' | 'merge' | 'reposition' | 'review' | 'exclude';

// Priority levels for posts requiring attention
export type PostPriority = 'critical' | 'high' | 'medium' | 'low';

// ─── Attention Type Key (July 2026 API spec) ────────────────────────────────

/** One entry in the attention_type_key legend returned by the BKA API. */
export interface AttentionTypeInfo {
  /** Short display label — use as badge text. */
  label: string;
  /** What the problem is — show in popover / expanded row. */
  description: string;
  /** What the user should do — show in expanded row / action panel. */
  action: string;
}

/**
 * Legend dictionary shipped alongside posts_requiring_attention.
 * Keys are attention_type values (e.g. "update", "rewrite", "retire", …).
 * Only present when posts_requiring_attention is non-empty.
 */
export type AttentionTypeKey = Record<string, AttentionTypeInfo>;

// ─── Attention Workflow (Done/Skip/Restore) ─────────────────────────────────
// See Specifications/posts-requiring-attention-workflow.md

export type AttentionWorkflowStatus = 'pending' | 'done' | 'skipped';

export interface AttentionWorkflowEntry {
  status: AttentionWorkflowStatus;
  statusChangedAt: string | null;
}

/** Keyed by `${post_id}:${action_type}` — matches the recommendation identity. */
export type AttentionWorkflowMap = Record<string, AttentionWorkflowEntry>;

export interface AttentionWorkflowTotals {
  pending: number;
  done: number;
  skipped: number;
  total: number;
}

// ─── Post ────────────────────────────────────────────────────────────────────

export interface PostNeedingAttention {
  // Core identifiers
  post_id: number;
  title?: string;           // May come from API or need lookup
  post_title?: string;      // Alternative field name from BKA API

  // ── July 2026 canonical fields (content_health_summary path) ──────────────
  /** Canonical attention classification (replaces action_type / potential_action). */
  attention_type?: string;
  /** AI rationale specific to this post (replaces detailed_reasoning). */
  rationale?: string;
  /** Connectivity tier at time of analysis: "Isolated" | "Emerging Connections" | "Well Connected" | "Content Hub" */
  connectivity_status?: string;
  /** Priority for types that carry one: "high" | "medium" | "low" */
  priority?: string;

  // ── Legacy fields — kept for backward-compat with stored analyses ──────────
  original_category?: string;
  categories?: string[];
  is_excluded?: boolean;
  isExcluded?: boolean;
  action_type?: PostActionType;
  potential_action?: string;
  update_priority?: PostPriority;
  rewrite_priority?: PostPriority;
  exclude_reason?: string;
  detailed_reasoning?: string;
  recommended_update?: string;
  rewrite_rationale?: string;
  retire_rationale?: string;
  action_rationale?: string;
  alternative_category?: string;
  edit_url?: string;
}

export interface CriticalFinding {
  critical_finding: string;
  recommendation: string;
}

export interface HubScoreBreakdown {
  depth: number;
  linkingCompatibility: number;
  authority: number;
  conceptBreadth: number;
  currentConnectivity: number;
}

export interface PostConnectivityScore {
  postId: number;
  postTitle: string;
  postTag: string | null;
  /** Live post URL — populated for HTML/no-CMS clients only (no HubSpot editor to link to). */
  postUrl?: string | null;
  connectivityScore: number;
  outboundLinks: number;
  inboundLinks: number;
  islandRole: 'hub' | 'spoke' | 'bridge' | 'standalone' | null;
  requiresAttention: boolean;
  attentionReasons: unknown;
  tier: 'Content Hub' | 'Well Connected' | 'Emerging Connections' | 'Isolated';
  // Hub metadata (v2.0)
  isHubPost?: boolean;
  hubCategory?: string | null;
  hubConnectivityStatus?: 'under_connected' | 'adequate' | 'strong' | null;
  hubTargetLinks?: number | null;
  hubSelectionScore?: number | null;
  hubDesignationReason?: string | null;
  hubDesignatedAt?: string | null;
  hubScoreBreakdown?: HubScoreBreakdown | null;
  progressToContentHub?: number | null;
}

export interface ContentAnalysisStatistics {
  totalPosts: number;
  contentIslands: number;
  postsInIslands: number;
  standalonePosts: number;
  coveragePercentage: number;
  semanticLinks: number;
  otherInternalLinks: number;
  postsRequiringAttention: number;
}

// ============================================
// Content Gaps Types
// ============================================

export interface ContentGap {
  topic: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  gapType: string;
  cluster?: string;
  relatedCluster?: string;
  recommendation?: string;
  contentDescription?: string;
  relatedPosts?: number[];
  connectsToPosts?: number[];
}

export interface TopValuablePost {
  postId: string | number;
  ranking: number;
  compositeScore: number;
}

export interface HowToReplicate {
  overview?: string;
  stepByStep?: { action: string; details?: string; exampleFromPosts?: string }[];
  quickChecklist?: string[];
}

export interface WritingTemplate {
  // Original expected fields
  suggestedStructure?: string[];
  lengthGuidance?: string;
  toneGuidance?: string;
  // Additional fields from BKA API
  overview?: string;
  keySections?: string[];
  targetAudience?: string;
  estimatedLength?: string;
  recommendedFormat?: string;
}

export interface ContentGuidance {
  whatMakesTheseWork?: string;
  keySuccessPatterns?: string[];
  howToReplicate?: HowToReplicate;
  writingTemplate?: WritingTemplate;
  whatToAvoid?: string[];
}

export interface CommonAttributes {
  averageTechnicalDepth?: number;
  dominantAuthorityTypes?: string[];
  commonConcepts?: string[];
}

export interface SuccessPatterns {
  patternInsights?: string[];
  commonAttributes?: CommonAttributes;
}

export interface CategoryValueProfile {
  overallStrength: 'high' | 'medium' | 'low';
  strengthRationale?: string;
  topPostsCoverage?: string;
  improvementOpportunities?: string[] | string;
}

export interface AnalysisMetadata {
  totalPostsAnalyzed: number;
  topPostsCount: number;
}

export interface HighValueAnalysis {
  category: string;
  analysisMetadata?: AnalysisMetadata;
  topValuablePosts?: TopValuablePost[];
  contentGuidance?: ContentGuidance;
  successPatterns?: SuccessPatterns;
  categoryValueProfile?: CategoryValueProfile;
}

export interface ContentGapsResponse {
  success: boolean;
  hasResults: boolean;
  analysisId?: number;
  analysisDate?: string;
  contentGaps: ContentGap[];
  highValueAnalyses: HighValueAnalysis[];
  totalGaps: number;
}

export interface ContentAnalysisData {
  success: boolean;
  hasData: boolean;
  analysis?: {
    id: number;
    sessionId: string;
    createdAt: string;
    postsAnalyzed: number;
  };
  topicClusters?: TopicCluster[];
  semanticRelationships?: SemanticRelationship[];
  postsNeedingAttention?: PostNeedingAttention[];
  /** Legend for attention_type values — present when postsNeedingAttention is non-empty. */
  attentionTypeKey?: AttentionTypeKey;
  /** Persisted Done/Skipped/Pending status, keyed by `${post_id}:${action_type}`. */
  attentionWorkflow?: AttentionWorkflowMap;
  /** Site-wide totals for the attention workflow, independent of any UI filtering. */
  attentionWorkflowTotals?: AttentionWorkflowTotals;
  criticalFinding?: CriticalFinding;
  connectivityScores?: Record<string, PostConnectivityScore>;
  statistics?: ContentAnalysisStatistics;
  linkStats?: {
    total: number;
    pending: number;
    applied: number;
  };
}

// Bridge between content islands (calculated on frontend)
export interface IslandBridge {
  island1: string;
  island2: string;
  connections: BridgeConnection[];
  averageStrength: number;
  connectionCount: number;
  relationshipTypes: string[];
}

export interface BridgeConnection {
  post1: number;
  post2: number;
  post1Title: string;
  post2Title: string;
  type: string;
  strength: number;
  explanation?: string;
  direction?: string;
}

// ============================================
// LICENSE API
// ============================================

export const licenseApi = {
  /**
   * Get license status for current site
   */
  getStatus: async (siteId: number): Promise<{ success: boolean; license: LicenseStatus }> => {
    const { data } = await api.get(`/api/sites/${siteId}/license`);
    return data;
  },

  /**
   * Activate license key
   */
  activate: async (siteId: number, licenseKey: string): Promise<{
    success: boolean;
    message?: string;
    license?: LicenseStatus;
    error?: string;
  }> => {
    const { data } = await api.post(`/api/license/activate`, { siteId, licenseKey });
    return data;
  },

  /**
   * Verify existing license (same as getStatus, for API consistency)
   */
  verify: async (siteId: number): Promise<{
    success: boolean;
    license?: LicenseStatus;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/license/status?siteId=${siteId}`);
    return data;
  },

  /**
   * Clear/deactivate license
   */
  clear: async (siteId: number): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/license/clear`, { siteId });
    return data;
  },
};

// ============================================
// CLAUDE API SETTINGS
// ============================================

export const claudeApi = {
  /**
   * Get Claude API status
   */
  getStatus: async (siteId: number): Promise<{ success: boolean; status: ClaudeApiStatus }> => {
    const { data } = await api.get(`/api/settings/claude/status?siteId=${siteId}`);
    return data;
  },

  /**
   * Test Claude API key
   */
  test: async (siteId: number, apiKey: string): Promise<{
    success: boolean;
    valid: boolean;
    message?: string;
  }> => {
    const { data } = await api.post(`/api/settings/claude/test`, { siteId, apiKey });
    return data;
  },

  /**
   * Save Claude API key.
   * Pass markValid: true when saving immediately after a successful test so the
   * backend stores the status as 'valid' rather than 'untested'.
   */
  save: async (siteId: number, apiKey: string, markValid = false): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/settings/claude`, { siteId, apiKey, markValid });
    return data;
  },

  /**
   * Background-verify the stored API key against Anthropic live.
   * Call on settings load when status is 'valid' to catch revoked/expired keys.
   * Returns { valid: boolean|null, status: 'valid'|'invalid'|'not_configured'|'unknown' }
   */
  verify: async (siteId: number): Promise<{ success: boolean; valid: boolean | null; status: string }> => {
    const { data } = await api.get(`/api/settings/claude/verify?siteId=${siteId}`);
    return data;
  },
};

// ============================================
// BLOG API
// ============================================

export const blogApi = {
  /**
   * Get blog tags (without counts - faster)
   */
  getTags: async (siteId: number): Promise<{ success: boolean; tags: BlogTag[] }> => {
    const { data } = await api.get(`/api/blog/tags?siteId=${siteId}`);
    return data;
  },

  /**
   * Get blog tags with post counts and language breakdown (slower but has counts)
   */
  getTagsWithCounts: async (portalId: string): Promise<{ 
    success: boolean; 
    tags: BlogTag[]; 
    total: number;
    totalPosts: number;
    totalEnglish?: number;
    totalNonEnglish?: number;
    languageBreakdown?: Record<string, number>;
  }> => {
    const { data } = await api.get(`/api/blog/tags/with-counts?portalId=${portalId}`);
    return data;
  },

  /**
   * Get blog posts count
   */
  getPostsCount: async (siteId: number): Promise<{ success: boolean; total: number }> => {
    const { data } = await api.get(`/api/blog/posts/count?siteId=${siteId}`);
    return data;
  },

  /**
   * Get blog posts (paginated)
   */
  getPosts: async (siteId: number, params?: {
    page?: number;
    limit?: number;
    tagIds?: string[];
  }): Promise<{ success: boolean; posts: BlogPost[]; total: number }> => {
    const queryParams = new URLSearchParams({ siteId: siteId.toString() });
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.tagIds?.length) queryParams.append('tagIds', params.tagIds.join(','));
    
    const { data } = await api.get(`/api/blog/posts?${queryParams.toString()}`);
    return data;
  },
};

// ============================================
// ANALYSIS API
// ============================================

/**
 * Incremental analysis stats
 */
export interface IncrementalAnalysisStats {
  total: number;      // Total posts in the blog
  new: number;        // New posts (not in previous analysis)
  changed: number;    // Changed posts (content hash differs)
  deleted: number;    // Deleted posts (in snapshots but not current)
  unchanged: number;  // Unchanged posts (skipped in incremental)
}

export const analysisApi = {
  /**
   * Start a new analysis
   * 
   * @param forceBaseline - true for full re-analysis, false for incremental (only new/changed)
   */
  start: async (siteId: number, params: {
    forceBaseline: boolean;
    selectedTagIds?: string[];
    crawlJobId?: string;   // Provided on phase-2 call after async crawl finishes
    /**
     * HTML-client post selection (migration 065). Only meaningful alongside
     * crawlJobId. Omit to fall back to whatever's already saved on the site;
     * pass explicitly (including null / []) to update the saved selection.
     */
    htmlMaxPosts?: number | null;
    htmlSelectedCategories?: string[] | null;
  }): Promise<{
    success: boolean;
    // Phase-1 response (HTML async crawl enqueued)
    phase?: 'crawling';
    jobId?: string;
    // Phase-2 / HubSpot response (analysis session created)
    sessionId?: string;
    sseUrl?: string;
    message?: string;
    analysisType?: 'hierarchical' | 'incremental';
    stats?: IncrementalAnalysisStats;
    postsCount?: number;
    totalPosts?: number;
    noChanges?: boolean;
    structuralExcluded?: number;
    error?: string;
    // Set when the backend rejected this start because an analysis for this
    // site is already running (409) — sessionId (above) and status identify
    // that session so the caller can reconnect to it instead of erroring.
    alreadyRunning?: boolean;
    status?: string;
  }> => {
    try {
      const { data } = await api.post(`/api/analysis/start`, {
        siteId,
        forceBaseline: params.forceBaseline,
        selectedTagIds: params.selectedTagIds,
        crawlJobId: params.crawlJobId,
        htmlMaxPosts: params.htmlMaxPosts,
        htmlSelectedCategories: params.htmlSelectedCategories,
      });
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const body = err.response.data || {};
        return {
          success: false,
          alreadyRunning: true,
          sessionId: body.sessionId,
          status: body.status,
          error: 'An analysis is already running for this site.',
        };
      }
      // Surface the backend's specific error message (e.g. "Crawl found 0
      // posts...") instead of letting the raw AxiosError's generic
      // "Request failed with status code 400" propagate — callers throw
      // new Error(startResponse.error) using this field.
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        return {
          success: false,
          error: err.response.data.error,
        };
      }
      throw err;
    }
  },

  /**
   * Upload posts batch
   */
  uploadBatch: async (siteId: number, params: {
    sessionId: string;
    batchNumber: number;
    totalBatches: number;
    posts?: BlogPost[];
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    const { data } = await api.post(`/api/analysis/upload-batch`, {
      siteId,
      ...params,
    });
    return data;
  },

  /**
   * Get analysis status
   */
  getStatus: async (siteId: number, sessionId: string): Promise<{
    success: boolean;
    status?: string;
    progress?: number;
    progressMessage?: string;
    currentStep?: string;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/analysis/status/${sessionId}?siteId=${siteId}`);
    return data;
  },

  /**
   * Fetch analysis results from BKA and store in database
   */
  fetchResults: async (siteId: number, sessionId: string): Promise<{
    success: boolean;
    analysisId?: number;
    results?: AnalysisResult;
    linkRecommendationsCount?: number;
    message?: string;
    error?: string;
  }> => {
    const { data } = await api.post(`/api/analysis/fetch-results`, { siteId, sessionId });
    return data;
  },

  /**
   * Cancel running analysis
   */
  cancel: async (siteId: number, sessionId: string): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/analysis/cancel`, { siteId, sessionId });
    return data;
  },

  /**
   * Resume a paused analysis (e.g., after adding API credits)
   * Called when analysis was paused due to BILLING_ERROR
   */
  resume: async (siteId: number, sessionId: string): Promise<{
    success: boolean;
    sessionId?: string;
    status?: string;
    message?: string;
    error?: string;
    stillPaused?: boolean;
  }> => {
    const { data } = await api.post(`/api/analysis/resume`, { siteId, sessionId });
    return data;
  },

  /**
   * Sync analysis status from SSE event to database
   * Called when frontend receives a paused/error SSE event to keep DB in sync
   */
  syncStatus: async (siteId: number, sessionId: string, status: string, errorType?: string, errorMessage?: string, progressMessage?: string): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const { data } = await api.post(`/api/analysis/sync-status`, { 
      siteId, 
      sessionId, 
      status,
      errorType,
      errorMessage,
      progressMessage
    });
    return data;
  },

  /**
   * Get active analysis
   */
  getActive: async (siteId: number): Promise<{
    success: boolean;
    hasActiveAnalysis: boolean;
    queue?: AnalysisQueue;  // Backend returns 'queue' not 'analysis'
  }> => {
    const { data } = await api.get(`/api/analysis/active?siteId=${siteId}`);
    return data;
  },

  /**
   * Get latest completed analysis results
   */
  getLatest: async (siteId: number): Promise<{
    success: boolean;
    hasResults: boolean;
    analysis?: AnalysisResult;
    linkRecommendations?: LinkRecommendationStats;
  }> => {
    const { data } = await api.get(`/api/analysis/latest?siteId=${siteId}`);
    return data;
  },

  /**
   * Get analysis history
   */
  getHistory: async (siteId: number, limit?: number): Promise<{
    success: boolean;
    analyses: AnalysisResult[];
  }> => {
    const { data } = await api.get(`/api/analysis/history?siteId=${siteId}&limit=${limit || 10}`);
    return data;
  },

  /**
   * Get analysis queue entries
   */
  getQueue: async (siteId: number, limit?: number): Promise<{
    success: boolean;
    entries: QueueEntry[];
    total: number;
  }> => {
    const { data } = await api.get(`/api/analysis/queue?siteId=${siteId}&limit=${limit || 5}`);
    return data;
  },

  /**
   * Get content analysis data (for Content Analysis page)
   * Returns topic clusters, semantic relationships, connectivity scores
   */
  getContentAnalysisData: async (siteId: number, analysisId?: number): Promise<ContentAnalysisData> => {
    const params = analysisId 
      ? `siteId=${siteId}&analysisId=${analysisId}`
      : `siteId=${siteId}`;
    const { data } = await api.get(`/api/analysis/content-analysis?${params}`);
    return data;
  },

  /**
   * Mark a Posts-Requiring-Attention recommendation Done/Skipped, or restore it
   * to Pending. Identity = (siteId, postId, actionType).
   * See Specifications/posts-requiring-attention-workflow.md
   */
  setAttentionStatus: async (
    siteId: number,
    postId: number,
    actionType: string,
    status: AttentionWorkflowStatus,
    metadata?: { priority?: string; postTitle?: string; rationale?: string },
  ): Promise<{ success: boolean; workflow: { post_id: number; action_type: string; status: AttentionWorkflowStatus; status_changed_at: string | null } }> => {
    const { data } = await api.post('/api/analysis/attention-status', {
      siteId,
      postId,
      actionType,
      status,
      priority: metadata?.priority,
      postTitle: metadata?.postTitle,
      rationale: metadata?.rationale,
    });
    return data;
  },

  /**
   * Get content gaps data for the Content Gaps page
   */
  getContentGapsData: async (siteId: number): Promise<ContentGapsResponse> => {
    const { data } = await api.get(`/api/analysis/content-gaps?siteId=${siteId}`);
    return data;
  },
};

// ============================================
// LINK RECOMMENDATIONS API
// ============================================

export const linksApi = {
  /**
   * Get pending link recommendation counts grouped by post ID
   * Used to determine which posts have pending recommendations
   */
  getPendingCountsByPost: async (siteId: number): Promise<{
    success: boolean;
    pendingByPost: Record<string, number>;
  }> => {
    const { data } = await api.get(`/api/links/pending-counts?siteId=${siteId}`);
    return data;
  },

  /**
   * Get link recommendations
   */
  getRecommendations: async (siteId: number, params?: {
    status?: string;
    categoryPair?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    postId?: string;  // Filter by related post (source OR target)
    postTitle?: string;  // Filter by post title, partial match (source OR target)
  }): Promise<{
    success: boolean;
    recommendations: LinkRecommendation[];
    stats: LinkRecommendationStats;
    total: number;
    filteredByPostId?: string | null;
  }> => {
    const queryParams = new URLSearchParams({ siteId: siteId.toString() });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.categoryPair) queryParams.append('categoryPair', params.categoryPair);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.postId) queryParams.append('postId', params.postId);
    if (params?.postTitle) queryParams.append('postTitle', params.postTitle);

    const { data } = await api.get(`/api/links/recommendations?${queryParams.toString()}`);
    return data;
  },

  /**
   * Get category pairs for filtering
   */
  getCategoryPairs: async (siteId: number): Promise<{
    success: boolean;
    categoryPairs: string[];
  }> => {
    const { data } = await api.get(`/api/links/category-pairs?siteId=${siteId}`);
    return data;
  },

  /**
   * Typeahead search for the "Filter by Post Title" box — returns distinct posts
   * (with a recommendation count) matching the partial title, so the user can pick
   * one and switch into the existing postId-filtered view.
   */
  searchPostTitles: async (siteId: number, query: string): Promise<{
    success: boolean;
    posts: { postId: string; title: string; linkCount: number }[];
  }> => {
    const { data } = await api.get(
      `/api/links/post-title-search?siteId=${siteId}&query=${encodeURIComponent(query)}`
    );
    return data;
  },

  /**
   * Mark recommendation as applied
   */
  markApplied: async (siteId: number, linkId: number): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/links/apply`, { siteId, linkId, action: 'implemented' });
    return data;
  },

  /**
   * Dismiss recommendation
   */
  dismiss: async (siteId: number, linkId: number): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/links/apply`, { siteId, linkId, action: 'dismissed' });
    return data;
  },

  /**
   * Revert recommendation back to pending (undo applied or dismissed)
   */
  setToPending: async (siteId: number, linkId: number): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/links/apply`, { siteId, linkId, action: 'pending' });
    return data;
  },

  /**
   * Prepare semantic link data for direct BKA call from frontend
   * Returns credentials, request data, structure state, and include_structure flag
   */
  prepareSemanticLink: async (siteId: number, linkId: number): Promise<PrepareSemanticLinkResult> => {
    const { data } = await api.get(`/api/links/${linkId}/semantic-prepare?siteId=${siteId}`);
    return data;
  },

  /**
   * Save semantic link result after direct BKA call.
   * Pass contentMd5 and sourcePostId so the backend can upsert post_structure_analysis.
   */
  saveSemanticResult: async (
    linkId: number,
    siteId: number,
    result: unknown,
    contentMd5?: string,
    sourcePostId?: string
  ): Promise<{
    success: boolean;
    structureAnalysisFailed?: boolean;
    data?: SemanticLinkResult;
    error?: string;
  }> => {
    const { data } = await api.post(`/api/links/${linkId}/semantic-result`, {
      siteId,
      result,
      contentMd5,
      sourcePostId
    });
    return data;
  },

  /**
   * Get stored semantic link result
   */
  getSemanticLinkStatus: async (siteId: number, linkId: number): Promise<{
    success: boolean;
    data?: SemanticLinkResult;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/links/semantic-text/status?siteId=${siteId}&linkId=${linkId}`);
    return data;
  },
};

// Semantic link result interface (matches backend snake_case response)
export interface SemanticLinkResult {
  from_post_id: number;
  from_post_title: string;
  to_post_id: number;
  to_post_title: string;
  to_post_url: string;
  recommended_placement: string | null;
  optimal_placement: string | null;
  placement_rationale: string | null;
  current_paragraph: string | null;
  recommended_replacement: string | null;
  anchor_text: string | null;
  relationship_type: string | null;
  confidence: number | null;
  match_anchor: string | null;
  processing_time_ms: number | null;
}

// ============================================
// RECOMMENDATION ACTIONS API
// ============================================

export const recommendationActionsApi = {
  /**
   * Mark recommendation action as complete
   */
  markComplete: async (siteId: number, params: {
    recommendationId: string;
    analysisId?: number;
    postId: number;
    userId?: number;
  }): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/api/recommendations/mark-complete`, { siteId, ...params });
    return data;
  },

  /**
   * Get completed recommendation actions
   */
  getCompleted: async (siteId: number, analysisId?: number): Promise<{
    success: boolean;
    completedActions: Array<{
      recommendationId: string;
      postId: number;
      completedAt: string;
    }>;
    totalCompleted: number;
  }> => {
    const params = new URLSearchParams({ siteId: siteId.toString() });
    if (analysisId) params.append('analysisId', analysisId.toString());
    const { data } = await api.get(`/api/recommendations/completed?${params.toString()}`);
    return data;
  },
};

// ============================================
// STRUCTURAL TAGS + STRUCTURE SETTINGS API
// ============================================

export interface HubSpotTagWithStructural {
  id: string;
  name: string;
  slug: string;
  isStructural: boolean;
}

export interface StructureSettings {
  enableStructureAnalysis: boolean;
  enableCategoryRecommendation: boolean;
}

export const structuralTagsApi = {
  getAll: async (siteId: number): Promise<{ success: boolean; tags: HubSpotTagWithStructural[]; structuralTagIds: string[] }> => {
    const { data } = await api.get(`/api/sites/${siteId}/structural-tags`);
    return data;
  },

  save: async (siteId: number, tagIds: string[]): Promise<{ success: boolean; structuralTagIds: string[] }> => {
    const { data } = await api.put(`/api/sites/${siteId}/structural-tags`, { tagIds });
    return data;
  },

  getSettings: async (siteId: number): Promise<{ success: boolean } & StructureSettings> => {
    const { data } = await api.get(`/api/sites/${siteId}/structure-settings`);
    return data;
  },

  saveSettings: async (siteId: number, settings: StructureSettings): Promise<{ success: boolean }> => {
    const { data } = await api.put(`/api/sites/${siteId}/structure-settings`, settings);
    return data;
  },
};

// ============================================
// STRUCTURE ANALYSIS ACTIONS API
// ============================================

export interface HeadingRecommendation {
  action: 'keep' | 'revise' | 'elevate' | 'demote' | 'add';
  seo_impact?: 'high' | 'medium' | 'low';
  impact?: 'high' | 'medium' | 'low';
  // BKA may return tag (string "h2") or level (number 2) — handle both
  existing_heading?: { tag?: string; level?: number; text?: string };
  recommended?: { tag?: string; level?: number; text?: string };
  reason?: string;
  placement?: {
    paragraph_starts_with?: string;
    insert_before?: string;
  };
}

export interface StructureAnalysis {
  recommendations: HeadingRecommendation[];
  analysis_summary?: {
    overall_score: number;
    word_count?: number;
    // BKA returns these field names
    recommended_heading_count?: number;
    current_heading_count?: number;
    // Legacy field names (kept for compatibility)
    recommended_headings?: number;
    current_headings?: number;
    key_issues?: string[];
    heading_counts?: { keep?: number; revise?: number; elevate?: number; demote?: number; add?: number };
  };
}

export interface StructureState {
  score: number | null;
  analysisDate: string | null;
  completedAt: string | null;
  allSkipped: boolean;
  recommendationsApplied: number[];
  recommendationsSkipped: number[];
  recommendationsManual: number[];
  undoData: Record<string, unknown>;
  categoryAcknowledged: boolean | null;
}

export interface PrimaryTagRecommendation {
  // BKA response field names (actual)
  recommended_primary?: string;
  reasoning?: string;
  category_fit_scores?: Array<{ category: string; fit_score?: number; fit_reasoning?: string }>;
  // Legacy / normalised field names (kept for compatibility)
  primary_tag?: string;
  primary_tag_fit?: number;
  confidence?: 'high' | 'medium' | 'low';
  reason?: string;
  all_tags?: Array<{ tag: string; fit?: number; reason?: string }>;
}

export interface PrepareSemanticLinkResult {
  success: boolean;
  alreadyGenerated?: boolean;
  /** Resolved target post URL — returned by the backend for both fresh and alreadyGenerated responses */
  targetUrl?: string;
  message?: string;
  credentials?: {
    licenseKey: string;
    domain: string;
    instanceKey: string;
    claudeApiKey: string;
  };
  bkaUrl?: string;
  requestData?: {
    from_post: { id: number; title: string; content: string; url?: string; categories?: Array<{ name: string; slug: string }> };
    to_post: { id: number; title: string; url: string };
    relationship: { type: string; anchor_text: string };
  };
  contentMd5?: string;
  includeStructure?: boolean;
  contentChangedSinceAnalysis?: boolean;
  cachedStructureAnalysis?: StructureAnalysis | null;
  cachedCategoryRecommendation?: PrimaryTagRecommendation | null;
  structureState?: StructureState | null;
  enableStructureAnalysis?: boolean;
  enableCategoryRecommendation?: boolean;
  linkId?: number;
  siteId?: number;
  error?: string;
}

export const structureApi = {
  applyHeading: async (siteId: number, postId: string, recIndex: number): Promise<{
    success: boolean;
    autoCompleted?: boolean;
    undoData?: unknown;
    error?: string;
    /** Machine-readable refusal reason on 409/422 (e.g. anchor_not_found, would_stack_headings, dnd_template). */
    code?: string;
  }> => {
    const { data } = await api.post('/api/links/structure/apply-heading', { siteId, postId, recIndex });
    return data;
  },

  skipHeading: async (siteId: number, postId: string, recIndex: number): Promise<{
    success: boolean;
    autoCompleted?: boolean;
  }> => {
    const { data } = await api.post('/api/links/structure/skip-heading', { siteId, postId, recIndex });
    return data;
  },

  manualHeading: async (siteId: number, postId: string, recIndex: number): Promise<{
    success: boolean;
    autoCompleted?: boolean;
  }> => {
    const { data } = await api.post('/api/links/structure/manual-heading', { siteId, postId, recIndex });
    return data;
  },

  undoHeading: async (siteId: number, postId: string, recIndex: number): Promise<{
    success: boolean;
    error?: string;
    /** Set on 409 when the post changed since the heading was applied (undo_conflict). */
    code?: string;
  }> => {
    const { data } = await api.post('/api/links/structure/undo-heading', { siteId, postId, recIndex });
    return data;
  },

  markComplete: async (siteId: number, postId: string): Promise<{ success: boolean }> => {
    const { data } = await api.post('/api/links/structure/complete', { siteId, postId });
    return data;
  },

  skipAll: async (siteId: number, postId: string): Promise<{ success: boolean; skippedCount?: number }> => {
    const { data } = await api.post('/api/links/structure/skip-all', { siteId, postId });
    return data;
  },

  requestReanalyze: async (siteId: number, postId: string): Promise<{ success: boolean }> => {
    const { data } = await api.post('/api/links/structure/reanalyze', { siteId, postId });
    return data;
  },

  ackCategory: async (siteId: number, postId: string, params: {
    action: 'apply' | 'dismiss';
    primaryTagName?: string;
  }): Promise<{ success: boolean; newTagIds?: string[]; error?: string }> => {
    const { data } = await api.post('/api/links/structure/ack-category', { siteId, postId, ...params });
    return data;
  },
};

// ============================================
// IMPLEMENT CATEGORY API
// ============================================

export const implementCategoryApi = {
  implement: async (siteId: number, params: {
    mode: 'implement' | 'manually_applied';
    recommendationId: string;
    /** Verbatim original/overbroad category name. */
    originalCategory: string;
    /** Verbatim AI-suggested new category name. */
    pseudoCategory: string;
    analysisId?: number;
    newTagName?: string;
    postIds?: number[];
    originalTagId?: string;
    originalTagAction?: 'mark_structural' | 'keep' | 'remove';
    /** 'manually_applied' only — the real name the user gave the category in the CMS. */
    wordpressCategory?: string;
  }): Promise<{
    success: boolean;
    mode?: string;
    newTagId?: string;
    newTagName?: string;
    postsUpdated?: number;
    structuralTagsUpdated?: boolean;
    message?: string;
    error?: string;
  }> => {
    const { data } = await api.post('/api/recommendations/implement-category', { siteId, ...params });
    return data;
  },
};

// ============================================
// DRAFT POST LINK RECOMMENDATIONS
// ============================================

export interface DraftPost {
  id: string;
  title: string;
  slug: string;
  createdAt: string | null;
  updatedAt: string | null;
  tags: Array<{ id: string; name: string }>;
  hasEligibleTags: boolean;
  pendingRecsCount: number;
}

export interface DraftLinksResult {
  success: boolean;
  draft_post_id: string | number;
  category: string;
  recommendations: Array<{
    target_post_id: string | number;
    target_post_title: string;
    anchor_text: string;
    relationship_type: string;
    relevance_score: number;
    priority_score: number;
    reason: string;
    temporal_compatibility: 'excellent' | 'good' | 'acceptable';
  }>;
  saved_count: number;
  processing_time_ms: number;
  structure_analysis?: object | null;
  primary_category_recommendation?: object | null;
  structure_analysis_failed?: boolean;
}

export interface DraftLinksPrepareResponse {
  success: boolean;
  credentials: {
    licenseKey: string;
    instanceKey: string;
    claudeApiKey: string;
    domain: string;
  };
  bkaUrl: string;
  bkaRequestBody: {
    postId: string;
    title: string;
    content: string;
    category: string;
  };
  includeStructure: boolean;
  contentMd5: string;
  siteId: number;
}

export const draftLinksApi = {
  getDraftPosts: async (siteId: string): Promise<{
    hasBaseline: boolean;
    posts: DraftPost[];
    total: number;
    error?: string;
    message?: string;
  }> => {
    const { data } = await api.get(`/api/blog/posts/drafts?siteId=${siteId}`);
    return data;
  },

  prepare: async (siteId: string, postId: string, category: string): Promise<DraftLinksPrepareResponse> => {
    const { data } = await api.get(
      `/api/links/draft-links/prepare?siteId=${siteId}&postId=${postId}&category=${encodeURIComponent(category)}`
    );
    return data;
  },

  saveStructure: async (
    siteId: number,
    sourcePostId: string,
    sourcePostTitle: string,
    category: string,
    result: DraftLinksResult,
    contentMd5: string
  ): Promise<void> => {
    await api.post('/api/links/draft-links/save-structure', {
      siteId, sourcePostId, sourcePostTitle, category, result, contentMd5,
    });
  },
};

// ============================================
// POST LINKS (Search by Title/URL — analyzed posts only)
// ============================================

export type PostLinksDirection = 'inbound' | 'outbound' | 'both';
export type PostLinksScope = 'intra' | 'inter' | 'both';

export interface AnalyzedPostCandidate {
  postId: string;
  title: string;
  url: string;
  analyzedAt: string;
  categories: string[];
}

export interface PostLinksRecommendation {
  direction: 'inbound' | 'outbound';
  counterpart_post_id: string | number;
  counterpart_post_title: string;
  counterpart_post_url?: string;
  counterpart_category?: string;
  anchor_text: string;
  relationship_type: string;
  relevance_score: number;
  priority_score: number;
  reason: string;
  temporal_compatibility: 'excellent' | 'good' | 'acceptable';
  link_type: 'intra_category' | 'inter_category';
}

export interface PostLinksResult {
  success: boolean;
  post_id: string | number;
  category: string;
  direction: PostLinksDirection;
  link_scope: PostLinksScope;
  recommendations: PostLinksRecommendation[];
  saved_count: number;
  processing_time_ms: number;
  warnings?: string[];
  structure_analysis?: object | null;
  primary_category_recommendation?: object | null;
  structure_analysis_failed?: boolean;
}

export interface PostLinksHistoryEntry {
  postId: string;
  postTitle: string | null;
  postUrl: string | null;
  category: string | null;
  direction: PostLinksDirection | null;
  linkScope: PostLinksScope | null;
  savedCount: number;
  totalRecommendations: number;
  generatedAt: string;
}

export interface PostLinksPrepareResponse {
  success: boolean;
  credentials: {
    licenseKey: string;
    instanceKey: string;
    claudeApiKey: string;
    domain: string;
  };
  bkaUrl: string;
  bkaRequestBody: {
    postId: string;
    title: string;
    url: string;
    content: string;
    category: string;
    direction: PostLinksDirection;
    link_scope: PostLinksScope;
  };
  includeStructure: boolean;
  contentMd5: string;
  siteId: number;
}

export const postLinksApi = {
  search: async (siteId: string, query: string): Promise<{
    success: boolean;
    candidates: AnalyzedPostCandidate[];
    error?: string;
    message?: string;
  }> => {
    const { data } = await api.get(
      `/api/links/post-links/search?siteId=${siteId}&query=${encodeURIComponent(query)}`
    );
    return data;
  },

  prepare: async (
    siteId: string,
    postId: string,
    direction: PostLinksDirection,
    linkScope: PostLinksScope,
    category?: string
  ): Promise<PostLinksPrepareResponse> => {
    const params = new URLSearchParams({ siteId, postId, direction, linkScope });
    if (category) params.append('category', category);
    const { data } = await api.get(`/api/links/post-links/prepare?${params.toString()}`);
    return data;
  },

  saveResult: async (
    siteId: number,
    focusPostId: string,
    focusPostTitle: string,
    category: string,
    result: PostLinksResult,
    contentMd5: string,
    focusPostUrl?: string,
    direction?: PostLinksDirection,
    linkScope?: PostLinksScope
  ): Promise<{ success: boolean; savedCount: number; warnings: string[] }> => {
    const { data } = await api.post('/api/links/post-links/save-result', {
      siteId, focusPostId, focusPostTitle, focusPostUrl, category, direction, linkScope, result, contentMd5,
    });
    return data;
  },

  /**
   * "Past Searches" — posts that have had lookup recommendations generated for them.
   */
  getHistory: async (siteId: string): Promise<{ success: boolean; searches: PostLinksHistoryEntry[] }> => {
    const { data } = await api.get(`/api/links/post-links/history?siteId=${siteId}`);
    return data;
  },

  getHistoryDetail: async (siteId: string, postId: string): Promise<{
    success: boolean;
    recommendations: PostLinksRecommendation[];
  }> => {
    const { data } = await api.get(
      `/api/links/post-links/history/detail?siteId=${siteId}&postId=${encodeURIComponent(postId)}`
    );
    return data;
  },
};

// ============================================
// BUSINESS CONTEXT
// ============================================

export interface BusinessContextIndustry {
  code: string;
  name: string;
  keywords: string[];
  pack_status: 'loaded' | 'not_loaded';
}

export interface BusinessContextSector {
  code: string;
  name: string;
  industries: BusinessContextIndustry[];
}

export interface BusinessContextProfile {
  industry_code: string;
  industry_name: string;
  business_model: 'B2B' | 'B2C' | 'B2B2C' | 'D2C';
  secondary_industry_codes?: string[] | null;
  pack_loaded: boolean;
  pack_note: string | null;
  updated_at: string;
}

export const BUSINESS_MODELS: Array<{ value: string; label: string; description: string }> = [
  { value: 'B2B',   label: 'Business-to-Business',              description: 'We sell to other companies' },
  { value: 'B2C',   label: 'Business-to-Consumer',              description: 'We sell directly to individuals' },
  { value: 'B2B2C', label: 'Business-to-Business-to-Consumer',  description: 'We sell through partners to consumers' },
  { value: 'D2C',   label: 'Direct-to-Consumer',                description: 'We manufacture and sell our own products directly' },
];

export const businessContextApi = {
  getIndustries: async (siteId: string | number): Promise<{
    success: boolean;
    data: { version: string; sectors: BusinessContextSector[] } | null;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/business-context/industries?siteId=${siteId}`);
    return data;
  },

  getProfile: async (siteId: string | number): Promise<{
    success: boolean;
    data: BusinessContextProfile | null;
    noLicense?: boolean;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/business-context?siteId=${siteId}`);
    return data;
  },

  saveProfile: async (
    siteId: string | number,
    industryCode: string,
    businessModel: string,
    secondaryIndustryCodes?: string[]
  ): Promise<{
    success: boolean;
    data: BusinessContextProfile | null;
    error?: string;
    message?: string;
    sync_warning?: string;
  }> => {
    const { data } = await api.post('/api/business-context', {
      siteId,
      industry_code: industryCode,
      business_model: businessModel,
      secondary_industry_codes: secondaryIndustryCodes ?? [],
    });
    return data;
  },
};

// ============================================
// SCHEMA API TYPES
// ============================================

export interface SchemaGlobalSettings {
  cmsType?: string;             // e.g. 'hubspot', 'wordpress' — drives import button label
  schemaOutputEnabled: boolean;
  enableFaqExtraction: boolean;
  maxFaqsPerPost: number;
  faqSectionTitle?: string | null;
  faqInjectionEnabled: boolean;
  faqCss?: string | null;
  html5InjectionEnabled?: boolean;
  html5FreshnessEnabled?: boolean;
  html5AuthorFooterEnabled?: boolean;
  html5FigureWrapEnabled?: boolean;
  /** HubSpot-side "Halo mode" / brand-association opt-in — see CLIENT-VIZZEX-BRAND-LINK-SYNC.md. */
  vizzexBrandLink?: boolean;
  /** True when site_url contains "vizzex.ai" — forces the effective brand-link value on regardless of the toggle. */
  isVizzexHost?: boolean;
  lastEntitySyncAt?: string | null;
  lastSavedAt?: string | null;         // last time entities were saved to DB (proxy: updated_at)
  lastPostSchemaFetchAt?: string | null;
  lastPageSchemaSubmitAt?: string | null;
}

export interface PageHeadingAnalysis {
  pageType: string;
  linkedEntityId?: string | null;
  cmsPageId?: string | null;
  score?: number | null;
  analysisDate?: string | null;
  analysisJson?: Record<string, unknown> | null;
}

export interface CmsAuthor {
  cmsId: string;
  name: string;
  email: string;
  bio: string;
  image: string;
  profileUrl: string;
  slug: string;
}

export interface SchemaOrganization {
  // Core identity
  name: string;
  legalName?: string;
  url: string;
  logo?: string;
  organizationType?: string;   // schema.org @type, e.g. 'Organization', 'LocalBusiness'
  // Social profiles → schema:sameAs
  sameAs?: string[];
  // About
  description?: string;
  email?: string;
  phone?: string;
  foundingDate?: string;       // ISO date: 'YYYY-MM-DD' or bare 'YYYY'
  // Location & Service Area
  streetAddress?: string;
  city?: string;
  stateRegion?: string;
  postalCode?: string;
  country?: string;            // ISO 2-letter code, e.g. 'US'
  areaServed?: string;         // Comma-separated region codes, e.g. 'US, CA, AU'
  // Location image — used to generate a stable ImageObject node and
  // ContactPage.primaryImageOfPage.  Omit key entirely (don't send null/{}
  // when empty) — the server treats a missing key as "no location image".
  locationImage?: {
    url: string;
    caption?: string;
  };
  // Typed contact points — emitted as Organization.contactPoint[] on Contact page.
  // Entity-level takes priority over any contact_points submitted at the page level.
  contactPoints?: ContactPoint[];
  // Subject areas the organisation is expert in — emitted as schema:knowsAbout[].
  knows_about?: string[];
}

export interface SchemaAuthor {
  name: string;               // required — schema:name
  url?: string;               // schema:url — profile/bio page
  image?: string;             // schema:image — headshot URL
  jobTitle?: string;          // schema:jobTitle
  isFounder?: boolean;        // schema:founder flag
  description?: string;       // schema:description — bio
  email?: string;             // schema:email
  sameAs?: string[];          // schema:sameAs — social profile URLs
  knowsAbout?: string[];      // schema:knowsAbout — E-E-A-T topics
  authorMapping?: string[];   // CMS identifiers for post-to-author matching
}

export interface SchemaProduct {
  /** Computed by server: slugify(name) || 'product_N'. Used as linked_entity_id for sub-page mapping. */
  entityId?: string;
  // Core — all types
  schemaType: string;          // 'SoftwareApplication' | 'ProfessionalService' | 'Product' | 'Course'
  name: string;                // required
  url: string;                 // required
  description?: string;
  image?: string;
  price?: string;              // numeric string e.g. '497'
  currency?: string;           // ISO 3-letter e.g. 'USD'
  sku?: string;
  // SoftwareApplication + Course/Training only
  features?: string[];         // feature_list array
  // Course-specific fields (only used when schemaType === 'Course')
  courseMode?: string;         // 'Online' | 'Blended' | 'Onsite'
  duration?: string;           // ISO 8601 e.g. 'P12M'
  instructors?: string[];      // author names selected as instructors
  courseStartDate?: string;    // ISO date e.g. '2026-06-17' → CourseInstance.startDate
  courseWorkload?: string;     // ISO 8601 duration e.g. 'PT5H' → CourseInstance.courseWorkload
  courseLocation?: string;     // VirtualLocation name or Place name
  inLanguage?: string | string[]; // BCP 47 tag(s) e.g. 'en-US' or ['en-US','es-MX']
  // SoftwareApplication-specific fields (only used when schemaType === 'SoftwareApplication')
  operatingSystem?: string;    // e.g. 'Web', 'iOS, Android'
  softwareVersion?: string;    // e.g. '3.2.1'
  screenshotUrls?: string[];   // SoftwareApplication.screenshot[]
  aggregateRating?: {
    ratingValue: number;       // average rating
    ratingCount: number;       // number of ratings
    reviewCount?: number;      // number of written reviews
    bestRating?: number;       // highest possible rating (default 5)
    worstRating?: number;      // lowest possible rating (default 1)
  };
}

export interface SchemaOffer {
  name: string;
  url: string;
  description?: string;
  offerType?: string;      // 'Service' | 'Consultation' | 'Course' | 'Download' | 'SoftwareLicense' | 'Promotion'
  billingPeriod?: string;  // 'one_time' | 'monthly' | 'annual' | 'free'
  isFree?: boolean;
  price?: string;
  currency?: string;
  availability?: string;   // schema.org ItemAvailability: 'InStock' | 'OutOfStock' | etc.
  validFrom?: string;      // YYYY-MM-DD
  validThrough?: string;   // YYYY-MM-DD
  appliesTo?: string[];    // product names this offer applies to
  checkoutUrl?: string;    // direct checkout/cart URL — emits BuyAction when set
  offerAudience?: 'business' | 'consumer' | 'both';
}

export interface PostSchemaRow {
  postId: string;
  postTitle?: string | null;
  postUrl?: string | null;
  injectionStatus: 'pending' | 'injected' | 'failed' | 'disabled';
  schemaDisplayEnabled: boolean;
  injectedAt?: string | null;
  injectionError?: string | null;
  schemaGeneratedAt?: string | null;
  updatedAt?: string | null;
}

export interface OpeningHoursSpec {
  day_of_week: string[];
  opens: string;
  closes: string;
  valid_from?: string | null;
  valid_through?: string | null;
}

export interface ContactPoint {
  contact_type: string;
  email?: string | null;
  telephone?: string | null;
  available_language?: string | string[] | null;
  hours_available?: OpeningHoursSpec | OpeningHoursSpec[] | null;
}

export interface PageSchemaRow {
  pageType: string;
  cmsPageId?: string | null;
  cmsPageName?: string | null;
  cmsPageUrl?: string | null;
  injectionStatus: 'pending' | 'injected' | 'failed' | 'skipped' | 'disabled';
  isInjectable: boolean;
  injectedAt?: string | null;
  injectionError?: string | null;
  pageSubtype?: string | null;
  contactPoints?: ContactPoint[] | null;
  linkedEntityId?: string | null;
}

export interface CmsPageOption {
  id: string;
  name: string;
  url: string;
  slug: string;
  isBlogListing?: boolean;
}

export interface FaqRow {
  id: number;
  question: string;
  answer: string;
  status: 'approved' | 'rejected' | 'pending' | null;
  sortOrder: number | null;
  /** BKA question ID — required for PATCH/DELETE on BKA */
  questionId: string | null;
  questionType: 'explicit' | 'implicit' | null;
  answerLocation: 'heading' | 'subheading' | 'paragraph' | 'list' | 'unknown' | null;
  confidence: number | null;
  relatedConcepts: string[] | null;
  excluded: boolean;
  excludedAt: string | null;
  excludedReason: string | null;
}

export interface FaqGroup {
  postId: string;
  postTitle: string | null;
  postUrl: string | null;
  faqsEnabled: boolean;
  faqs: FaqRow[];
}

export interface PageMappingEntry {
  cmsPageId: string | null;
  cmsPageUrl?: string | null;
  cmsPageName?: string | null;
  isInjectable?: boolean;
  pageSubtype?: string | null;
  contactPoints?: ContactPoint[] | null;
  linkedEntityId?: string | null;
}

/** One product_page or service_page sub-page mapping. */
export interface SubPageMappingEntry {
  pageType:        'product_page' | 'service_page';
  linkedEntityId:  string;   // slugify(entity.name) — must match BKA entity id
  entityName:      string;   // display only
  cmsPageId:       string | null;
  cmsPageUrl?:     string | null;
  cmsPageName?:    string | null;
  injectionStatus?: string;
  isInjectable?:   boolean;
  injectedAt?:     string | null;
  injectionError?: string | null;
}

/** One pricing page mapping (may be multiple per site, keyed by cmsPageId). */
export interface PricingMappingEntry {
  cmsPageId:       string | null;
  cmsPageUrl?:     string | null;
  cmsPageName?:    string | null;
  injectionStatus?: string;
  isInjectable?:   boolean;
  injectedAt?:     string | null;
  injectionError?: string | null;
}

/**
 * One glossary root page.
 *
 * `linkedEntityId` is required when the tenant has more than one glossary (it
 * becomes the BKA storage-key suffix). For single-glossary sites using Mode A,
 * it may be null — the server uses the bare `glossary` key.
 *
 * `hasGlossarySubpages`:
 *   false → Mode A (AI term extraction from page HTML)
 *   true  → Mode B (client-supplied term sub-pages, no HTML required)
 */
export interface GlossaryMappingEntry {
  linkedEntityId:      string | null;
  cmsPageId:           string | null;
  cmsPageUrl?:         string | null;
  cmsPageName?:        string | null;
  hasGlossarySubpages: boolean;
  /** Mode B root description (DefinedTermSet scope) — required in Mode B. */
  description?:        string | null;
  injectionStatus?:    string;
  isInjectable?:       boolean;
  injectedAt?:         string | null;
  injectionError?:     string | null;
}

/**
 * One glossary term sub-page (Mode B only).
 *
 * `linkedEntityId` must be stable and unique across all glossary_term rows for
 * the tenant. Recommended: last URL path segment, prefixed with the glossary id
 * when slugs could collide across different glossaries.
 */
export interface GlossaryTermMappingEntry {
  /** Stable slug unique per tenant term (e.g. "sfku", "signals-cri"). */
  linkedEntityId:      string;
  cmsPageId:           string | null;
  cmsPageUrl?:         string | null;
  cmsPageName?:        string | null;
  /** Absolute URL of the parent glossary root page. */
  parentGlossaryUrl?:  string | null;
  /** Display name of the parent glossary (used as breadcrumb label). */
  parentGlossaryName?: string | null;
  /** Optional abbreviation/code for the term (e.g. "MFA", "SFKU"). */
  termCode?:           string | null;
  /** Term definition — becomes DefinedTerm.description. Required for BKA. */
  description?:        string | null;
  injectionStatus?:    string;
  injectedAt?:         string | null;
  injectionError?:     string | null;
}

// ============================================
// SCHEMA API
// ============================================

export const schemaApi = {
  getGlobalSettings: async (siteId: number | string): Promise<SchemaGlobalSettings> => {
    const { data } = await api.get(`/api/schema/global-settings?siteId=${siteId}`);
    return data;
  },

  saveGlobalSettings: async (siteId: number | string, settings: Partial<SchemaGlobalSettings>): Promise<void> => {
    await api.post('/api/schema/global-settings', { siteId, ...settings });
  },

  getEntities: async (siteId: number | string): Promise<{
    organization: SchemaOrganization | null;
    authors: SchemaAuthor[];
    products: SchemaProduct[];
    offers: SchemaOffer[];
    faqSettings: { enableFaqExtraction: boolean; maxFaqsPerPost: number };
    vizzexBrandLink: boolean;
    isVizzexHost: boolean;
    lastSyncAt: string | null;
    configHash: string | null;
  }> => {
    const { data } = await api.get(`/api/schema/entities?siteId=${siteId}`);
    return data;
  },

  /** Save entities to database only — does NOT sync to BKA. */
  saveEntities: async (
    siteId: number | string,
    payload: {
      organization?: SchemaOrganization | null;
      authors?: SchemaAuthor[];
      products?: SchemaProduct[];
      offers?: SchemaOffer[];
      enableFaqExtraction?: boolean;
      maxFaqsPerPost?: number;
      vizzexBrandLink?: boolean;
    }
  ): Promise<{ success: boolean; synced: false; configHash: string }> => {
    const { data } = await api.post('/api/schema/entities', { siteId, ...payload, syncToBka: false });
    return data;
  },

  /** Save entities to database AND push to BKA schema generator. */
  saveAndSyncEntities: async (
    siteId: number | string,
    payload: {
      organization?: SchemaOrganization | null;
      authors?: SchemaAuthor[];
      products?: SchemaProduct[];
      offers?: SchemaOffer[];
      enableFaqExtraction?: boolean;
      maxFaqsPerPost?: number;
      vizzexBrandLink?: boolean;
    }
  ): Promise<{
    success: boolean;
    synced: boolean;
    configHash: string;
    regenerationRequired?: { all_posts?: boolean; all_pages?: boolean } | null;
  }> => {
    const { data } = await api.post('/api/schema/entities', { siteId, ...payload, syncToBka: true });
    return data;
  },

  fetchAndInjectPosts: async (siteId: number | string): Promise<{
    success: boolean;
    skipped?: boolean;
    reason?: string;
    /** Present on 202 async path — poll getJobStatus for results */
    jobId?: string;
    status?: string;
    /** Present on synchronous path (skipped only) */
    fetched?: number;
    upserted?: number;
    unchanged?: number;
    injected?: number;
    failed?: number;
    disabled?: number;
    faqsStored?: number;
  }> => {
    const { data } = await api.post('/api/schema/posts/fetch-and-inject', { siteId, force: true });
    return data;
  },

  getJobStatus: async (jobId: string, siteId: number | string): Promise<{
    status: 'processing' | 'done' | 'failed';
    result?: {
      /** post injection fields */
      fetched?: number;
      upserted?: number;
      unchanged?: number;
      skipped?: number;
      injected?: number;
      failed?: number;
      disabled?: number;
      faqInjected?: number;
      faqFailed?: number;
      faqsStored?: number;
      /**
       * Result of BKA's POST /schema/posts/regenerate-all, as returned within this job.
       * null when regeneration was skipped (entity config unchanged since last run) — not a failure.
       * success is only true when BOTH failedPosts and persistFailedPosts are empty — a later
       * GET /schema/posts returning data is NOT proof every post was rebuilt, so rely on this.
       */
      regenerateAllResult?: {
        success: boolean;
        totalPosts: number | null;
        schemasGenerated: number | null;
        schemasSaved: number | null;
        /** Schema build itself failed for these post IDs (not a save miss) */
        failedPosts: string[];
        /** Schema built but not saved — show these to the user; safe to re-run Regenerate Post Schema */
        persistFailedPosts: string[];
        sessionId: string | null;
        generatedAt: string | null;
        error?: string;
      } | null;
      /** page submission fields */
      warnings?: string[];
      schemaOutputEnabled?: boolean;
    } | null;
    error?: string | null;
    startedAt: string;
    completedAt: string | null;
  }> => {
    const { data } = await api.get(`/api/schema/posts/job-status?jobId=${jobId}&siteId=${siteId}`);
    return data;
  },

  fetchPostSchemasOnly: async (siteId: number | string): Promise<{
    success: boolean;
    fetched: number;
    upserted: number;
    unchanged: number;
  }> => {
    const { data } = await api.post('/api/schema/posts/fetch', { siteId });
    return data;
  },

  listPostSchemas: async (
    siteId: number | string,
    params?: { status?: string; page?: number; limit?: number }
  ): Promise<{ posts: PostSchemaRow[]; total: number; page: number; limit: number }> => {
    const qs = new URLSearchParams({ siteId: String(siteId) });
    if (params?.status) qs.set('status', params.status);
    if (params?.page)   qs.set('page',   String(params.page));
    if (params?.limit)  qs.set('limit',  String(params.limit));
    const { data } = await api.get(`/api/schema/posts?${qs}`);
    return data;
  },

  togglePostSchema: async (siteId: number | string, postId: string, enabled: boolean): Promise<void> => {
    await api.post(`/api/schema/posts/${postId}/toggle`, { siteId, enabled });
  },

  getCmsPages: async (siteId: number | string, refresh = false): Promise<{
    pages: CmsPageOption[];
    total: number;
    cachedAt: string | null;
  }> => {
    const { data } = await api.get(
      `/api/schema/pages/cms-list?siteId=${siteId}${refresh ? '&refresh=true' : ''}`
    );
    return data;
  },

  getPageMapping: async (siteId: number | string): Promise<{
    mapping: PageSchemaRow[];
    subPageMappings: SubPageMappingEntry[];
    pricingMappings: PricingMappingEntry[];
    glossaryMappings: GlossaryMappingEntry[];
    glossaryTermMappings: GlossaryTermMappingEntry[];
  }> => {
    const { data } = await api.get(`/api/schema/pages/mapping?siteId=${siteId}`);
    return {
      mapping:              data.mapping              ?? [],
      subPageMappings:      data.subPageMappings      ?? [],
      pricingMappings:      data.pricingMappings      ?? [],
      glossaryMappings:     data.glossaryMappings     ?? [],
      glossaryTermMappings: data.glossaryTermMappings ?? [],
    };
  },

  savePageMapping: async (
    siteId: number | string,
    mapping: Record<string, PageMappingEntry | null>,
    subPages?: Array<{
      pageType: 'product_page' | 'service_page';
      linkedEntityId: string;
      cmsPageId: string | null;
      cmsPageUrl?: string | null;
      cmsPageName?: string | null;
    }>,
    pricingPages?: Array<{
      cmsPageId: string | null;
      cmsPageUrl?: string | null;
      cmsPageName?: string | null;
    }>,
    glossaryMappings?: GlossaryMappingEntry[],
    glossaryTermMappings?: GlossaryTermMappingEntry[]
  ): Promise<void> => {
    await api.post('/api/schema/pages/mapping', {
      siteId, mapping, subPages, pricingPages,
      glossaryMappings, glossaryTermMappings,
    });
  },

  submitPagesToBka: async (siteId: number | string): Promise<{
    success: boolean;
    /** Present on 202 async path — poll getJobStatus for results */
    jobId?: string;
    status?: string;
    /** Present on synchronous fallback path */
    injected?: number;
    failed?: number;
    warnings?: string[];
    schemaOutputEnabled?: boolean;
  }> => {
    const { data } = await api.post('/api/schema/pages/submit', { siteId });
    return data;
  },

  injectPosts: async (siteId: number | string, postIds?: string[]): Promise<{
    success: boolean;
    injected: number;
    failed: number;
    disabled: number;
  }> => {
    const { data } = await api.post('/api/schema/inject/posts', { siteId, postIds });
    return data;
  },

  injectPages: async (siteId: number | string, pageTypes?: string[]): Promise<{
    success: boolean;
    injected: number;
    failed: number;
    skipped: number;
  }> => {
    const { data } = await api.post('/api/schema/inject/pages', { siteId, pageTypes });
    return data;
  },

  clearInjection: async (
    siteId: number | string,
    scope: 'posts' | 'pages' | 'all',
    ids?: string[]
  ): Promise<{ success: boolean; cleared: number }> => {
    const { data } = await api.post('/api/schema/inject/clear', { siteId, scope, ids });
    return data;
  },

  listFaqs: async (siteId: number | string, postId?: string): Promise<{ faqs: FaqGroup[] }> => {
    const qs = new URLSearchParams({ siteId: String(siteId) });
    if (postId) qs.set('postId', postId);
    const { data } = await api.get(`/api/schema/faqs?${qs}`);
    return data;
  },

  updateFaqStatus: async (
    siteId: number | string,
    postId: string,
    faqId: number,
    status: 'approved' | 'rejected'
  ): Promise<void> => {
    await api.post(`/api/schema/faqs/${postId}/status`, { siteId, faqId, status });
  },

  /** Pull all FAQs from BKA and refresh local DB */
  fetchFaqsFromBka: async (siteId: number | string): Promise<{ success: boolean; totalStored: number; postCount: number }> => {
    const { data } = await api.post('/api/schema/faqs/fetch', { siteId });
    return data;
  },

  /** Exclude or include a single FAQ via BKA */
  updateFaqExclusion: async (
    siteId: number | string,
    postId: string,
    questionId: string,
    excluded: boolean,
    excludedReason?: string
  ): Promise<void> => {
    await api.patch(`/api/schema/faqs/${postId}/${questionId}`, { siteId, excluded, excluded_reason: excludedReason });
  },

  /** Toggle FAQ inclusion on/off for an entire post */
  togglePostFaqs: async (
    siteId: number | string,
    postId: string,
    enabled: boolean
  ): Promise<void> => {
    await api.post('/api/schema/faqs/toggle', { siteId, postId, enabled });
  },

  getCmsAuthors: async (siteId: number | string): Promise<{ authors: CmsAuthor[]; cmsType: string }> => {
    const { data } = await api.get(`/api/schema/cms-authors?siteId=${siteId}`);
    return data;
  },

  injectFaqs: async (siteId: number | string): Promise<{
    success: boolean;
    injected: number;
    failed: number;
    skipped: number;
    reason?: string;
  }> => {
    const { data } = await api.post('/api/schema/inject/faqs', { siteId });
    return data;
  },

  listPageHeadingAnalyses: async (siteId: number | string): Promise<{ analyses: PageHeadingAnalysis[] }> => {
    const { data } = await api.get(`/api/schema/pages/heading-analysis?siteId=${siteId}`);
    return data;
  },

  getPageHeadingAnalysisStatus: async (jobId: string): Promise<{
    success: boolean;
    jobId?: string;
    status?: 'queued' | 'processing' | 'completed' | 'failed';
    data?: {
      pageType: string;
      linkedEntityId: string | null;
      pageName: string;
      cmsPageId: string;
      score: number | null;
      analysisJson: Record<string, unknown> | null;
      processingTimeMs?: number | null;
    };
    processingTimeMs?: number | null;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/schema/pages/heading-analysis/status/${jobId}`);
    return data;
  },

  analyzePageHeadingStructure: async (
    siteId: number | string,
    pageType: string,
    entityId?: string | null
  ): Promise<{
    success: boolean;
    pageType: string;
    linkedEntityId: string | null;
    pageName: string;
    cmsPageId: string;
    score: number | null;
    analysisJson: Record<string, unknown> | null;
  }> => {
    // Step 1 — enqueue (returns 202 with jobId)
    const enqueueResp = await api.post('/api/schema/pages/heading-analysis/analyze', {
      siteId, pageType, ...(entityId ? { entityId } : {}),
    });
    const { jobId } = enqueueResp.data as { jobId: string };
    if (!jobId) throw new Error('No jobId returned from enqueue');

    // Step 2 — poll every 4 s for up to 2 minutes (30 attempts)
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    for (let i = 0; i < 30; i++) {
      await sleep(4000);
      const statusResp = await api.get(`/api/schema/pages/heading-analysis/status/${jobId}`);
      const poll = statusResp.data as {
        success: boolean;
        status?: string;
        data?: Record<string, unknown>;
        error?: string;
      };
      if (!poll.success) throw new Error('JOB_NOT_FOUND — please re-submit');
      if (poll.status === 'completed' && poll.data) {
        return poll.data as {
          success: boolean;
          pageType: string;
          linkedEntityId: string | null;
          pageName: string;
          cmsPageId: string;
          score: number | null;
          analysisJson: Record<string, unknown> | null;
        };
      }
      if (poll.status === 'failed') throw new Error(poll.error || 'Analysis failed');
      // queued | processing → keep polling
    }
    throw new Error('Timed out after 2 minutes — please try again');
  },

  savePageHeadingAnalysis: async (
    siteId: number | string,
    pageType: string,
    cmsPageId: string,
    result: unknown,
    contentMd5: string,
    entityId?: string | null
  ): Promise<{ success: boolean; score: number | null }> => {
    const { data } = await api.post('/api/schema/pages/heading-analysis/save', {
      siteId, pageType, cmsPageId, result, contentMd5,
      ...(entityId ? { entityId } : {}),
    });
    return data;
  },

  getCmsNativeSchemaStatus: async (siteId: number | string): Promise<{
    cmsType: string;
    blogs: Array<{
      id: string;
      name: string;
      absoluteUrl: string | null;
      nativeSchemaEnabled: boolean;
    }>;
    anyEnabled: boolean | null;
    unsupported?: boolean;
    error?: string;
  }> => {
    const { data } = await api.get(`/api/schema/cms-native-schema?siteId=${siteId}`);
    return data;
  },

};

/** Category name + post count, aggregated from a completed crawl's per-post `categories`. */
export interface HtmlPostCategory {
  name: string;
  count: number;
}

/** The site's currently saved post-selection settings (migration 065). */
export interface HtmlPostSelection {
  maxPosts: number | null;
  categories: string[] | null;
  /** false = user has never confirmed a selection yet — analysis.js gates on this. */
  configured: boolean;
}

export interface CrawlJobStatus {
  jobId: string;
  siteId: number;
  status: 'queued' | 'crawling' | 'extracting' | 'done' | 'error';
  postsFound: number;
  postsExtracted: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only present once status === 'done' */
  totalPosts?: number;
  categories?: HtmlPostCategory[];
  savedSelection?: HtmlPostSelection;
}

export interface CrawlJobInfo extends CrawlJobStatus {
  /** High-level phase hint from the backend */
  phase: 'crawling' | 'done' | 'error' | null;
}

export const crawlJobsApi = {
  getStatus: async (jobId: string): Promise<CrawlJobStatus> => {
    const { data } = await api.get(`/api/crawl-jobs/${jobId}`);
    return data;
  },

  /** Returns the most recent crawl job for a site (last 48 h), or { job: null }. */
  getLatest: async (siteId: number): Promise<{ job: CrawlJobInfo | null }> => {
    const { data } = await api.get(`/api/sites/${siteId}/crawl-jobs/latest`);
    return data;
  },
};

export default {
  license: licenseApi,
  claude: claudeApi,
  blog: blogApi,
  analysis: analysisApi,
  crawlJobs: crawlJobsApi,
  links: linksApi,
  recommendationActions: recommendationActionsApi,
  structuralTags: structuralTagsApi,
  structure: structureApi,
  implementCategory: implementCategoryApi,
  draftLinks: draftLinksApi,
  postLinks: postLinksApi,
  businessContext: businessContextApi,
  schema: schemaApi,
};


